import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  Grid,
  Select,
  Text
} from '@chakra-ui/react'
import type { AxiosError, AxiosResponse } from 'axios'
import type {
  EditOrganizationUnit,
  ModelState,
  OrganizationUnit,
  OrganizationUnitProperties
} from 'BrokerTsLib/src/dtos'
import {
  BadRequestError
} from 'BrokerTsLib/src/dtos'
import type {
  OrganizationUnitType
} from 'brokertslib/src/enums'
import {
  getOrganizationUnitTypeName
} from 'brokertslib/src/enums'
import { useGetAcquisitionNumbers } from 'brokertslib/src/hooks/useOrganizationUnit'
import { FormProvider, useFieldArray, useForm } from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'brokertslib/src/Utils/formModelState'
import { useNavigate } from 'react-router-dom'

import ErrorSummary from '@/components/ErrorSummary'
import { FormGroup } from '@/components/FormGroup'
import { FormLabel } from '@/components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '@/components/FormSection'
import { FormSpacer } from '@/components/FormSpacer'
import { InputGroup } from '@/components/InputGroup'
import { PhoneInputGroup } from '@/components/PhoneInputGroup'
import { useFindAddress } from '@/hooks'
import { useGetAres } from '@/hooks/useUser'
import { ArrowLeft, Cloud } from '@/icons'
import { InputFormControl, TextareaFormControl, YesNoFormControl } from 'BrokerTsLib/src/components'

interface OrganizationUnitEditFormProps {
  organization: OrganizationUnit
  properties: OrganizationUnitProperties
  onSubmit: (
    values: EditOrganizationUnit,
  ) => Promise<AxiosResponse | AxiosError>
  possibleTypes: OrganizationUnitType[]
  isCreate?: boolean
}

export const OrganizationEditForm = ({
  organization,
  properties,
  onSubmit,
  possibleTypes,
  isCreate,
}: OrganizationUnitEditFormProps) => {
  const navigate = useNavigate()
  const methods = useForm<EditOrganizationUnit>({
    defaultValues: {
      ...organization,
      properties: { ...properties },
      acquisitionNumbers: [],
    },
    mode: 'onSubmit',
  })
  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setError,
    setValue,
    trigger,
  } = methods

  useGetAcquisitionNumbers(
    organization.id,
    organization.superiorOrganizationUnitId,
    {
      onSuccess: (data) => {
        const sortedData = data.data.sort((a, b) =>
          a.insurerName.localeCompare(b.insurerName),
        )

        reset((value) => ({ ...value, acquisitionNumbers: sortedData }))
      },
    },
  )

  const { fields } = useFieldArray({
    name: 'acquisitionNumbers',
    control,
  })

  const acquisitionNumbers = watch('acquisitionNumbers')

  const submitHandler = (data: EditOrganizationUnit) =>
    onSubmit(data).then((value) => {
      const e = value as AxiosError
      if (e.response?.status === 400)
        setModelStateErrors(
          setError,
          new BadRequestError(
            e.response.data as ModelState<EditOrganizationUnit>,
          ),
        )
    })

  const ico = watch('ico')

  const {
    data: aresData,
    refetch: refetchAres,
    isLoading: isAresLoading,
  } = useGetAres(ico, {
    enabled: false,
    onSuccess: (data) => {
      setValue('name', data.data.nazev)
    },
  })

  const ulice = aresData?.data.adresaUlice
    ? aresData?.data.adresaUlice
    : aresData?.data.adresaCastObce
      ? aresData?.data.adresaCastObce
      : aresData?.data.adresaObec

  const { isLoading: isRuianLoading } = useFindAddress(
    ulice,
    aresData?.data.adresaCislo,
    {
      enabled: !!ulice && !!aresData?.data.adresaCislo,
      onSuccess: (data) => {
        if (data) {
          const pscFiltered = data.filter(
            (d) => d.psc == aresData?.data.adresaPsc,
          )
          if (pscFiltered.length == 1) {
            const item = pscFiltered[0]
            setValue('addressStreet', item.ulice ?? '')
            let cp: string = item.cisloDomovni ?? ''
            if (item.cisloOrientacni) {
              cp = cp + '/' + item.cisloOrientacni
            }
            setValue('addressStreetNumber', cp)
            setValue('addressPostCode', item.psc)
            let obec = item.obec
            if (item.castObce && item.castObce != item.obec) {
              obec = obec + ' - ' + item.castObce
            }
            setValue('addressCity', obec)

            trigger('addressStreet')
            trigger('addressStreetNumber')
            trigger('addressPostCode')
            trigger('addressCity')
          }
        }
      },
    },
  )
  const isAddressLoading = isRuianLoading || isAresLoading

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <input type="hidden" {...register('id')} />

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Informace o organizaci</FormSectionTitle>

            <FormSectionDescription>
              Základní informace o organizaci.
            </FormSectionDescription>
          </FormSectionHeader>

          <FormSectionBody>
            <FormGroup sx={{ columnGap: 4, w: 'full' }}>
              <FormControl isRequired isInvalid={!!errors.type}>
                <FormLabel>Typ organizace</FormLabel>

                <Select
                  {...register('type', {
                    valueAsNumber: true,
                    validate: (value) =>
                      !value && value !== 0 ? 'Vyberte typ organizace' : true,
                  })}
                >
                  <option value="">Vyberte typ organizace</option>
                  {possibleTypes.map((type) => (
                    <option key={type} value={type}>
                      {getOrganizationUnitTypeName(type)}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>
                  {errors.type && errors.type.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl flexGrow={1} isRequired isInvalid={!!errors.name}>
                <FormLabel htmlFor="name">Název</FormLabel>
                <InputGroup
                  id="name"
                  placeholder="Název organizace"
                  errors={errors.name}
                  {...register('name', {
                    required: 'Zadejte název organizace',
                    maxLength: 256,
                  })}
                  sx={{ w: 'full' }}
                />
              </FormControl>
            </FormGroup>

            <TextareaFormControl
              name='description'
              control={control}
              label='Popis'
              placeholder="Popis organizace"
              w='full'
              maxLength={2000}
            />

            <FormGroup columnGap={4}
              alignItems="end">
              <InputFormControl
                name='ico'
                control={control}
                label='IČO'
                placeholder="Identifikační číslo firmy"
                pattern="\d+"
                maxLength={20}
              />
              <Button
                variant="outline"
                isLoading={isAddressLoading}
                isDisabled={
                  isAddressLoading || ico?.length < 8 || ico?.length > 10
                }
                onClick={() => refetchAres()}
              >
                <Cloud color="secondary.500" />
                Načíst údaje
              </Button>
              {aresData?.data?.errorMessage && (
                <Text color="red.600">{aresData.data.errorMessage}</Text>
              )}
            </FormGroup>

            <FormSpacer />

            <InputFormControl
              name='nationalBankLicenseCode'
              control={control}
              label='Kód ČNB'
              placeholder="Kód přidělený ČNB"
              maxLength={256}
            />

            <FormSpacer />

            <FormGroup>
              <InputFormControl
                name='addressStreet'
                control={control}
                label='Ulice'
                placeholder="Např. Dlouhá"
                htmlSize={24}
              />

              <InputFormControl
                name='addressStreetNumber'
                control={control}
                label='Č.p. a č.o.'
                placeholder="Např. 123"
                htmlSize={6}
              />

              <InputFormControl
                name='addressPostCode'
                control={control}
                label='PSČ'
                placeholder="Např. 110 00"
                pattern="[\d\s]+"
              />

              <InputFormControl
                name='addressCity'
                control={control}
                label='Město'
                placeholder="Např. Praha"
              />
            </FormGroup>

            {!isCreate && (
              <>
                <FormSpacer />
                <YesNoFormControl
                  name='valid'
                  control={control}
                  label='Aktivní'
                  size='md'
                />
              </>
            )}
          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Kontaktní osoba</FormSectionTitle>

            <FormSectionDescription>
              Zadejte kontakty na kontaktní osobu ogranizace.
            </FormSectionDescription>
          </FormSectionHeader>

          <FormSectionBody>
            <InputFormControl
              name='contactName'
              control={control}
              label='Celé jméno'
              placeholder="Např. Jan Novák"
              maxLength={100}
            />

            <FormGroup>
              <FormControl>
                <FormLabel>Telefon</FormLabel>

                <PhoneInputGroup input={register('phone', { maxLength: 20 })} />
              </FormControl>

              <FormControl>
                <FormLabel>Mobilní telefon</FormLabel>

                <PhoneInputGroup
                  input={register('mobilePhone', { maxLength: 20 })}
                />
              </FormControl>

              <InputFormControl
                name='email'
                control={control}
                label='E-mail'
                placeholder="Např. jan.novak@okgroup.cz"
                maxLength={256}
                type="email"
              />
            </FormGroup>
          </FormSectionBody>
        </FormSection>

        {acquisitionNumbers?.length > 0 && (
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Ziskatelská čísla</FormSectionTitle>

              <FormSectionDescription>
                Zadejte ziskatelská čísla u jednotlivých pojišťoven.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <Box
                sx={{
                  display: 'grid',
                  gridAutoFlow: 'column',
                  gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
                  gridTemplateRows: `repeat(${Math.round(acquisitionNumbers.length / 2)}, auto)`,
                  columnGap: 16,
                  rowGap: 4,
                }}
              >
                {fields.map((field, index) => {
                  return (
                    <InputFormControl
                      name={`acquisitionNumbers.${index}.acquisitionNumber`}
                      control={control}
                      label={field.insurerName}
                      key={field.id}
                      maxLength={256}
                    />
                  )
                })}

                <InputFormControl
                  name='properties.allianzTravelAgentCode'
                  control={control}
                  label='Allianz Travel'
                  maxLength={256}
                />
              </Box>
            </FormSectionBody>
          </FormSection>
        )}

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nastavení aplikace</FormSectionTitle>

            <FormSectionDescription>
              Dostupná nastavení aplikace pro danou organizaci.
            </FormSectionDescription>
          </FormSectionHeader>

          <FormSectionBody>
            <InputFormControl
              name='properties.webPage'
              control={control}
              label='Webové stránky'
              w='full'
              placeholder="Např. https://www.okgroup.cz/"
            />

            <TextareaFormControl
              name='properties.footerInfo'
              control={control}
              label='E-mail - zápatí'
              w='full'
              placeholder="Napište text, který se zobrazí v zápatí e-mailu."
            />

            <TextareaFormControl
              name='properties.informationMemorandumHeader'
              control={control}
              label='Poučení subjektu - záhlaví'
              w='full'
              placeholder="Napište poučení subjektu, které se zobrazí v záhlaví."
            />

            <TextareaFormControl
              name='properties.informationMemorandumBottom'
              control={control}
              label='Poučení subjektu - zápatí'
              w='full'
              placeholder="Napište poučení subjektu, které se zobrazí v zápatí."
            />

            <TextareaFormControl
              name='properties.businessRegister'
              control={control}
              label='Obchodní rejstřík'
              w='full'
              placeholder="Poznámka o zápisu firmy do obchodního rejstříku."
            />

            <FormGroup>
              <YesNoFormControl
                name='properties.businessSubject'
                control={control}
                label='Právnická osoba'
                size='md'
              />

              <YesNoFormControl
                name='properties.preContractDocument2ndLogo'
                control={control}
                label='Zobrazit logo OK Brokers v ZzJ'
                size='md'
              />
            </FormGroup>

            <FormGroup>
              <InputFormControl name='properties.complaintsEmail' control={control} label="E-mail pro stížnosti" />
              <InputFormControl name='properties.dataBox' control={control} label="Datová schránka" />
              <InputFormControl name='properties.complaintProcedureLink' control={control} label="Odkaz na postup pro stížnost" />
            </FormGroup>

            <FormGroup>
              <YesNoFormControl
                name='properties.policyHolderEmailRequired'
                control={control}
                label='Povinné vyplnění emailu pojistníka'
                size='md'
              />
              
              <YesNoFormControl
                name='properties.allowGcpDigitalSignature'
                control={control}
                label='Povolit digitální podpis GČP'
                size='md'
                defaultChecked={true}
              />
            </FormGroup>
          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nastavení pojištění vozidel</FormSectionTitle>
          </FormSectionHeader>
          <FormSectionBody>
            <TextareaFormControl
              name='properties.preContractDocumentLinks'
              control={control}
              label='Odkazy na dokumenty'
              w='full'
              placeholder="Odkazy na dokumenty ke stažení"
            />

            <InputFormControl
              name='properties.ipidLink'
              control={control}
              label='Odkaz na IPID'
              placeholder="Odkaz na IPIDy"
            />

            <FormGroup>
              <YesNoFormControl
                name='properties.allianzNoYearkm'
                control={control}
                label='Autobazar'
                size='md'
              />

              <YesNoFormControl
                name='properties.showPreferredLiquidation'
                control={control}
                label='Zobrazit Preferovaný způsob likvidace škod'
                size='md'
              />
            </FormGroup>
          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>
              Nastavení pojištění majetku a odpovědnosti
            </FormSectionTitle>
          </FormSectionHeader>
          <FormSectionBody>
            <TextareaFormControl
              name='properties.preContractDocumentLinks2'
              control={control}
              label='Odkazy na dokumenty'
              w='full'
              placeholder="Odkazy na dokumenty ke stažení"
            />

            <InputFormControl
              name='properties.ipidLink2'
              control={control}
              label='Odkaz na IPID'
              placeholder="Odkaz na IPIDy"
            />

          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nastavení cestovního pojištění</FormSectionTitle>
          </FormSectionHeader>
          <FormSectionBody>
            <TextareaFormControl
              name='properties.preContractDocumentLinks3'
              control={control}
              label='Odkazy na dokumenty'
              w='full'
              placeholder="Odkazy na dokumenty ke stažení"
            />

            <InputFormControl
              name='properties.ipidLink3'
              control={control}
              label='Odkaz na IPID'
              placeholder="Odkaz na IPIDy"
            />

          </FormSectionBody>
        </FormSection>

        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nastavení loginů a hesel</FormSectionTitle>
            <FormSectionDescription>
              Zadejte loginy a hesla k jednotlivým systémům pojišťoven.
              <br /> Login a heslo oddělte pomocí znaku "|" bez mezer.
            </FormSectionDescription>
          </FormSectionHeader>
          <FormSectionBody>
            <InputFormControl
              name='properties.csobCredentials'
              control={control}
              label='ČSOB Pojišťovna, a. s.'
              placeholder="login|heslo"
              pattern=".+\|.+"
            />

            <InputFormControl
              name='properties.pillowCredentials'
              control={control}
              label='Pillow pojišťovna a.s.'
              placeholder="login|heslo"
              pattern=".+\|.+"
            />

            <InputFormControl
              name='properties.pVzpCredentials'
              control={control}
              label='Pojišťovna VZP, a.s.'
              placeholder="login|heslo"
              pattern=".+\|.+"
            />

            <InputFormControl
              name='properties.axaAssistanceCredentials'
              control={control}
              label='AXA ASSISTANCE CZ, s.r.o.'
              placeholder="login|heslo"
              pattern=".+\|.+"
            />

            <InputFormControl
              name='properties.colonnadeTravelCredentials'
              control={control}
              label='Colonnade Insurance S.A.'
              placeholder="login|heslo|získatel Colonnade"
              pattern=".+\|.+\|.+"
            />

            <Divider />
          </FormSectionBody>
        </FormSection>

        <ErrorSummary errors={errors} />

        <Grid
          sx={{
            width: '100%',
            gridTemplateColumns: 'auto auto',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="ghost"
            isLoading={isSubmitting}
            type="button"
            onClick={() =>
              navigate(
                '/organization/' +
                (organization.id ??
                  organization.superiorOrganizationUnitId ??
                  'list'),
              )
            }
          >
            <ArrowLeft color="secondary.500" />
            Zpět
          </Button>
          <Button
            colorScheme="primary"
            isLoading={isSubmitting}
            onClick={() => {
              handleSubmit(submitHandler)()
            }}
          >
            {isCreate ? 'Vytvořit organizaci' : 'Uložit'}
          </Button>
        </Grid>
      </form>
    </FormProvider>
  )
}
